import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import useAddRoleSupervisor from 'modules/job_catalog/hooks/useAddRoleSupervisor'

const getEmployeeUser = (employee: Employee) => {
  const userId = employee.attributes.access_id
  const user = users.get(userId)
  if (!user) {
    throw Error(`Error getting users with id = ${userId}`)
  }
  return user
}

interface Props {
  roleId: number
  employees: Employee[]
  onClose: () => void
}

const AddRoleSupervisorModal: React.FC<Props> = ({ employees, onClose, roleId }) => {
  const [selectedIds, setSelectedIds] = React.useState<number[]>([])
  const addRoleSupervisor = useAddRoleSupervisor()

  const handleSubmit = async () => {
    const result = await addRoleSupervisor(roleId, selectedIds)
    const { success } = handleResult(result, () => undefined)

    if (success) {
      onClose()
      track(trackingEvents.jobCatalog.addRoleSupervisorSubmit)
    }
  }

  const handleClose = () => {
    onClose()
  }

  const options = employees.map((x) => {
    const user = getEmployeeUser(x)
    const name = getFullDisplayName({
      firstName: user.attributes.first_name,
      lastName: user.attributes.last_name,
      canSharePreferredName: x.policy.can('sharePreferredName'),
      preferredName: x.attributes.preferred_name,
    })
    return (
      <Selector.Option
        key={x.attributes.id}
        value={x.attributes.id}
        label={name}
        searchValue={name}
      >
        {name}
      </Selector.Option>
    )
  })

  return (
    <Modal
      illustration="multiple-buildings"
      title={i18n.t('job_catalog.add_role_supervisors')}
      description={i18n.t('job_catalog.add_role_supervisors_description')}
      footer={
        <Button disabled={!selectedIds.length} submit>
          {i18n.t('common.submit')}
        </Button>
      }
      onSubmit={handleSubmit}
      onClose={handleClose}
    >
      <Selector.Container
        value={selectedIds}
        blankLabel={i18n.t('common.empty')}
        onChange={(ids) => setSelectedIds(ids as number[])}
        searchPlaceholder={i18n.t('common.search')}
        headerLabel={i18n.t('common.name')}
        isMultiSelect
      >
        {options}
      </Selector.Container>
    </Modal>
  )
}

export default observer(AddRoleSupervisorModal)
